import { useState } from 'react'
import { api, type AccountDetail } from '../lib/api.ts'
import { Checkbox } from './Checkbox.tsx'
import { EntityForm, Field, FieldGroup } from './EntityForm.tsx'
import { useToast } from './Toast.tsx'

/**
 * What an account is, its page's Dados tab (#688).
 *
 * The name and whether quotations are read before approval are editable; the
 * identifier and the catalogue's status are shown. Saved apart from every
 * other tab: a name is not saved by switching a rule, nor a rule by renaming.
 */
export function AccountDetails({
  account,
  onSaved,
}: {
  account: AccountDetail
  onSaved: (next: AccountDetail) => void
}) {
  const [name, setName] = useState(account.name)
  const [earlyCapture, setEarlyCapture] = useState(account.earlyQuotationCapture)
  const [saving, setSaving] = useState(false)
  const toast = useToast()

  const dirty = name.trim() !== account.name || earlyCapture !== account.earlyQuotationCapture

  async function save() {
    setSaving(true)
    try {
      const r = await api.updateAccount(account.id, {
        name: name.trim(),
        earlyQuotationCapture: earlyCapture,
      })
      onSaved(r.account)
      setName(r.account.name)
      setEarlyCapture(r.account.earlyQuotationCapture)
      toast.show('Conta salva', { tone: 'success' })
    } catch (cause) {
      toast.show(cause instanceof Error ? cause.message : 'falha ao salvar', { tone: 'error' })
    } finally {
      setSaving(false)
    }
  }

  return (
    <EntityForm onSave={() => void save()} saving={saving} dirty={dirty}>
      <Field label="Nome" value={name} editable onChange={setName} />
      <Field label="Identificador" value={account.slug} />
      <Field label="Filiais" value={String(account.branches)} />
      <Field label="Usuários" value={String(account.people)} />
      <Field
        label="Catálogo sincronizado"
        value={
          account.catalogSyncedAt === undefined
            ? 'nunca'
            : new Date(account.catalogSyncedAt).toLocaleString('pt-BR', {
                dateStyle: 'short',
                timeStyle: 'short',
              })
        }
      />
      {/* Shown, never edited: the only internal account is ours, and a
          second one is made by hand on purpose. */}
      <Field label="Tipo" value={account.internal ? 'Interna' : 'Cliente'} />

      {/* Off by default, and turned on one pharmacy at a time (#437):
          it reads another table on their live database and queues
          quotations that may never be approved. */}
      <FieldGroup
        label="Conferência do orçamento"
        helperText="Lê cada orçamento assim que ele é digitado, antes da aprovação, para que um erro seja apontado antes de o orçamento chegar ao cliente. Se o orçamento for alterado ou aprovado depois da leitura, a conferência é refeita sozinha enquanto ninguém a abriu, mantendo o que já foi respondido; depois de aberta, a tela avisa e oferece ler de novo. Orçamentos que não são aprovados em 3 dias são encerrados."
      >
        <label className="flex items-start gap-3">
          <Checkbox
            checked={earlyCapture}
            disabled={saving}
            onChange={(e) => setEarlyCapture(e.target.checked)}
          />
          <span className="flex flex-col">
            <span className="text-tP text-text-strong">
              Conferir o orçamento antes da aprovação
            </span>
            <span className="text-tSmall text-text-weak">
              desligado por padrão — ligue para uma farmácia de cada vez
            </span>
          </span>
        </label>
      </FieldGroup>
    </EntityForm>
  )
}
